import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import FinanceLayout from 'src/feature/finance/FinanceLayout'
import {calculateLoanMetrics, parseAnnualRate} from './loanCalculator'
import './style.css'
import {NumericFormat} from 'react-number-format'
import ABCBankImg from './assets/abc-bank.png'
import DemoBankImg from './assets/demo-bank.png'
import MyBankImg from './assets/my-bank.png'
import {BreadCrumb} from 'src/_metronic/layout/components'

type Step = 'comparison' | 'step1' | 'step2' | 'success'

interface LoanOffer {
  id: string
  bankName: string
  annualRate: number
  minRevenue: number
  processingFee: string
  fundingTime: string
  features: string[]
  img: any
}

const LOAN_OFFERS: LoanOffer[] = [
  {
    id: '1',
    bankName: 'Demo Bank',
    annualRate: parseAnnualRate('12.55%'),
    minRevenue: 120000,
    processingFee: '1.0%',
    fundingTime: '24 hours',
    features: ['Inventory or short‑term financing', 'Smaller or time‑sensitive requests'],
    img: DemoBankImg,
  },
  {
    id: '2',
    bankName: 'My Bank',
    annualRate: parseAnnualRate('11.89%'),
    minRevenue: 90000,
    processingFee: '0.5%',
    fundingTime: '48 hours',
    features: ['Ongoing working capital needs', 'Seasonal cash‑flow support'],
    img: MyBankImg,
  },
  {
    id: '3',
    bankName: 'ABC Bank',
    annualRate: parseAnnualRate('10.95%'),
    minRevenue: 150000,
    processingFee: '1.5%',
    fundingTime: '72 hours',
    features: ['Equipment or asset expansion', 'Longer repayment periods'],
    img: ABCBankImg,
  },
]

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatRate(value: number) {
  return `${(value * 100).toFixed(2)}%`
}

const BusinessLoanPage = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('comparison')
  const [selectedLoanId, setSelectedLoanId] = useState<string>(LOAN_OFFERS[0].id)
  const [loanAmount, setLoanAmount] = useState(50000)
  const [tenureYears, setTenureYears] = useState(4)
  const [monthlyIncome, setMonthlyIncome] = useState('')
  const [sortBy, setSortBy] = useState<'payment' | 'rate'>('payment')

  const offersWithMetrics = [...LOAN_OFFERS]
    .map((offer) => ({
      ...offer,
      metrics: calculateLoanMetrics(
        loanAmount,
        offer.annualRate,
        tenureYears,
        parseAnnualRate(offer.processingFee)
      ),
    }))
    .sort((left, right) => {
      if (sortBy === 'rate') {
        return left.annualRate - right.annualRate
      }

      return left.metrics.monthlyPayment - right.metrics.monthlyPayment
    })

  const selectedLoan =
    offersWithMetrics.find((offer) => offer.id === selectedLoanId) ?? offersWithMetrics[0]
  const selectedMetrics = selectedLoan.metrics

  const renderSelectionSummary = () => (
    <div className='card border border-gray-200 shadow-sm pt-5'>
      <div className='card-body pt-2'>
        <div className='d-flex justify-content-between fs-5 mb-3 fw-bold'>
          <span className=''>Lender</span>
          <span className='fw-bold'>{selectedLoan.bankName}</span>
        </div>
        <div className='d-flex justify-content-between fs-5 mb-3'>
          <span className='fw-bold'>Loan amount</span>
          <span className='fw-bold'>{formatCurrency(loanAmount)}</span>
        </div>
        <div className='d-flex justify-content-between fs-5 mb-3'>
          <span className='fw-bold'>Term</span>
          <span className='fw-bold'>{selectedMetrics.termMonths} months</span>
        </div>
        <div className='d-flex justify-content-between fs-5 mb-3'>
          <span className='fw-bold'>APR</span>
          <span className='fw-bold'>{formatRate(selectedLoan.annualRate)}</span>
        </div>
        <div className='separator my-4' />
        <div className='d-flex justify-content-between fs-5'>
          <span className='fw-bold'>Monthly payment</span>
          <span className='fw-bold text-primary'>
            {formatCurrency(selectedMetrics.monthlyPayment)}
          </span>
        </div>
      </div>
    </div>
  )

  return (
    <div className=' dashboard-apple-page'>
      {step === 'comparison' ? (
        <div className='row g-6'>
          <div className='d-flex gap-5 align-items-center'>
            <button
              className='btn btn-primary btn-sm btn-back'
              onClick={() => {
                navigate('/finance')
              }}
            >
              <i className='fa fa-arrow-left'></i>
            </button>
            <BreadCrumb currentPage='Loan Calculator' />
          </div>

          <div className='col-xl-4'>
            <div className='card border border-gray-200 shadow-sm sticky-top'>
              <div className='card-header border-0 pt-6'>
                <div>
                  <h3 className='card-title fw-bold text-secondary'>Loan Calculator</h3>
                  <span className=' fw-semibold fs-7'>
                    Adjust loan details and compare estimated monthly payments across offers.
                  </span>
                </div>
              </div>
              <div className='card-body pt-2 d-flex flex-column gap-6'>
                <div>
                  <div className='d-flex justify-content-between mb-2'>
                    <label className='form-label fw-bold text-uppercase fs-8 mb-0'>
                      Loan Amount
                    </label>
                    <span className='fw-bold text-primary'>{formatCurrency(loanAmount)}</span>
                  </div>
                  <input
                    type='range'
                    className='form-range'
                    min='5000'
                    max='500000'
                    step='5000'
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number.parseInt(e.target.value, 10))}
                  />
                  <div className='d-flex justify-content-between  fs-8'>
                    <span>$5,000</span>
                    <span>$500,000</span>
                  </div>
                </div>

                <div>
                  <div className='d-flex justify-content-between mb-2'>
                    <label className='form-label fw-bold text-uppercase fs-8 mb-0'>Term</label>
                    <span className='fw-bold text-primary'>
                      {tenureYears} years / {tenureYears * 12} months
                    </span>
                  </div>
                  <input
                    type='range'
                    className='form-range'
                    min='1'
                    max='10'
                    value={tenureYears}
                    onChange={(e) => setTenureYears(Number.parseInt(e.target.value, 10))}
                  />
                </div>

                <div>
                  <label className='form-label fw-bold text-uppercase fs-8'>
                    Average Monthly Revenue
                  </label>
                  <div className='input-group'>
                    <span className='input-group-text'>$</span>
                    <NumericFormat
                      className='form-control'
                      decimalScale={2}
                      thousandSeparator
                      placeholder='0.00'
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                    />
                  </div>
                </div>

                {/*      <div className='rounded bg-light p-5'>
                  <div className=' text-uppercase fw-bold fs-8 mb-2 text-secondary'>
                    Selected scenario
                  </div>
                  <div className='fw-bold fs-2 text-gray-900 mb-1'>
                    {formatCurrency(selectedMetrics.monthlyPayment)}
                  </div>
                  <div className=''>
                    Estimated monthly payment at {formatRate(selectedLoan.annualRate)} APR
                  </div>
                  <div className='separator my-4' />
                  <div className='d-flex justify-content-between fs-7 mb-2'>
                    <span className=''>Total repayment</span>
                    <span className='fw-bold'>{formatCurrency(selectedMetrics.totalPayment)}</span>
                  </div>
                  <div className='d-flex justify-content-between fs-7'>
                    <span className=''>Total interest</span>
                    <span className='fw-bold'>{formatCurrency(selectedMetrics.totalInterest)}</span>
                  </div>
                </div> */}

                <div
                  className='rounded p-4'
                  style={{
                    backgroundColor: '#FDEADA',
                  }}
                >
                  <div className='d-flex align-items-center gap-3 mb-2'>
                    <i className='bi bi-info-circle text-primary fs-3' />
                    <span className='fw-bold text-gray-900'>Eligibility signal</span>
                  </div>
                  <p className=' mb-0'>
                    {/* Monthly revenue entered: {formatCurrency(monthlyIncome)}. Use this to compare
                    against each lender's minimum annual revenue. */}
                    The revenue entered is used to check eligibility against each lender’s
                    requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className='col-xl-8'>
            <div className='d-flex flex-column gap-6'>
              {offersWithMetrics.map((offer) => (
                <div key={offer.id} className='card border border-gray-200 shadow-sm'>
                  <div className='card-body p-7'>
                    <div className='row g-6 h-100 min-h-100'>
                      <div className='col-lg-3 d-flex'>
                        <div className='rounded bg-light p-5 text-center w-100 d-flex flex-column justify-content-center'>
                          {/* <div className='fw-bold fs-4 text-gray-900 mb-1'>{offer.bankName}</div> */}
                          <div className='fw-bold fs-4 text-gray-900 mb-1'>
                            <img src={offer.img} width={100} className='w-100 max-w-100' />
                          </div>
                          <span className='badge badge-light align-self-center'>
                            Funding in {offer.fundingTime}
                          </span>
                        </div>
                      </div>
                      <div className='col-lg-9'>
                        <div className='row g-4 mb-6'>
                          <div className='col-sm-3'>
                            <span className=' text-uppercase fw-bold fs-8 d-block mb-1 text-secondary'>
                              APR
                            </span>
                            <div className='fw-bold fs-2 text-gray-900'>
                              {formatRate(offer.annualRate)}
                            </div>
                          </div>
                          <div className='col-sm-3'>
                            <span className=' text-uppercase fw-bold fs-8 d-block mb-1 text-secondary'>
                              Processing fee
                            </span>
                            <div className='fw-bold fs-2 text-gray-900'>{offer.processingFee}</div>
                          </div>
                          <div className='col-sm-3'>
                            <span className=' text-uppercase fw-bold fs-8 d-block mb-1 text-secondary'>
                              Monthly payment
                            </span>
                            <div className='fw-bold fs-2 text-primary'>
                              {formatCurrency(offer.metrics.monthlyPayment)}
                            </div>
                          </div>
                          <div className='col-sm-3'>
                            <span className=' text-uppercase fw-bold fs-8 d-block mb-1 text-secondary'>
                              Total payment
                            </span>
                            <div className='fw-bold fs-2 text-primary'>
                              {formatCurrency(offer.metrics.totalPayment)}
                            </div>
                          </div>
                        </div>
                        <div className='d-flex justify-content-between align-items-center'>
                          <div className='d-flex flex-column gap-2'>
                            <div className='d-flex align-items-center gap-2  fs-7'>
                              <i className='bi bi-building-check text-success' />
                              Minimum annual revenue: {formatCurrency(offer.minRevenue)}
                            </div>
                            {offer.features.map((feature) => (
                              <div key={feature} className='d-flex align-items-center gap-2  fs-7'>
                                <i className='bi bi-check-circle-fill text-success' />
                                {feature}
                              </div>
                            ))}
                          </div>

                          <div className='d-flex align-items-start justify-content-end'>
                            <button
                              className='btn btn-primary'
                              onClick={() => {
                                setSelectedLoanId(offer.id)
                                setStep('step1')
                              }}
                            >
                              Start your application
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {step === 'step1' ? (
        <>
          <div className='row g-6 mt-10'>
            <div className='col-xl-8'>
              <div className='card border border-gray-200 shadow-sm'>
                <div className='card-body p-7 p-lg-8'>
                  <div className='d-flex justify-content-between align-items-center mb-6'>
                    <div className=''>
                      <div className='dashboard-apple-eyebrow text-uppercase fw-semibold mb-1'>
                        Step 1 of 2
                      </div>
                      <h3 className='fs-3 fw-bold text-gray-900 mb-0'>
                        Business contact information
                      </h3>
                    </div>
                  </div>

                  <div className='row g-5 mb-6'>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Full name</label>
                      <input
                        type='text'
                        className='form-control form-control-solid'
                        placeholder='Jane Doe'
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Business legal name</label>
                      <input
                        type='text'
                        className='form-control form-control-solid'
                        placeholder='Acme LLC'
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Email address</label>
                      <input
                        type='email'
                        className='form-control form-control-solid'
                        placeholder='jane@company.com'
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Phone number</label>
                      <input
                        type='text'
                        className='form-control form-control-solid'
                        placeholder='(555) 000-0000'
                      />
                    </div>
                  </div>

                  <div className='row g-5 py-2'>
                    <div>
                      <h3 className='fw-bold text-gray-900 mb-1'>
                        Operating and financial details
                      </h3>
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Industry</label>
                      <select className='form-select form-select-solid'>
                        <option>Retail</option>
                        <option>Technology</option>
                        <option>Professional Services</option>
                      </select>
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Years in business</label>
                      <select className='form-select form-select-solid'>
                        <option>1-2 years</option>
                        <option>3-5 years</option>
                        <option>5+ years</option>
                      </select>
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Monthly revenue</label>
                      <div className='input-group input-group-solid'>
                        <span className='input-group-text'>$</span>
                        <input
                          type=''
                          className='form-control form-control-solid'
                          value={monthlyIncome}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-bold'>Use of funds</label>
                      <select className='form-select form-select-solid'>
                        <option>Inventory purchase</option>
                        <option>Equipment upgrade</option>
                        <option>Working capital</option>
                      </select>
                    </div>
                  </div>
                  <div className='d-flex justify-content-start align-items-center mt-10 gap-5'>
                    <span
                      className='d-inline-flex cursor-pointer align-items-center gap-2 text-primary fw-bold fs-7 text-secondary'
                      onClick={() => setStep('comparison')}
                    >
                      <i className='bi bi-arrow-left text-secondary'></i>
                      Back to offers
                    </span>
                    <button className='btn btn-primary' onClick={() => setStep('step2')}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-xl-4'>{renderSelectionSummary()}</div>
          </div>
        </>
      ) : null}

      {step === 'step2' ? (
        <div className='row g-6 mt-10'>
          <div className='col-xl-8'>
            <div className='card border border-gray-200 shadow-sm'>
              <div className='card-body p-7 p-lg-8'>
                <div className='d-flex justify-content-between align-items-center mb-6'>
                  <div className=''>
                    <div className='dashboard-apple-eyebrow text-uppercase fw-semibold mb-1'>
                      Step 2 of 2
                    </div>
                    <h3 className='fs-3 fw-bold text-gray-900 mb-0'>Upload Supporting Documents</h3>
                  </div>
                </div>
                <div className='d-flex flex-column gap-4'>
                  {[
                    'Proof of identity',
                    'Last 6 months of bank statements',
                    'Recent financial statements',
                  ].map((item) => (
                    <div
                      key={item}
                      className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-4 rounded bg-light p-5'
                    >
                      <div>
                        <div className='fw-bold text-secondary'>{item}</div>
                        <span className=''>PDF, JPG or PNG up to 10MB</span>
                      </div>
                      <button className='btn btn-text text-secondary'>Upload</button>
                    </div>
                  ))}
                </div>
                <div className='d-flex justify-content-start align-items-center mt-10 gap-5'>
                  <span
                    className='d-inline-flex cursor-pointer align-items-center gap-2 text-primary fw-bold fs-7 text-secondary'
                    onClick={() => setStep('step1')}
                  >
                    <i className='bi bi-arrow-left text-secondary'></i>
                    Back to offers
                  </span>
                  <button className='btn btn-primary' onClick={() => setStep('success')}>
                    Submit Application
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='col-xl-4'>
            {renderSelectionSummary()}
            <div className='card border border-gray-200 shadow-sm mt-6'>
              <div className='card-body p-7'>
                <h4 className='fw-bold text-gray-900 mb-4'>Repayment snapshot</h4>
                <div className='d-flex justify-content-between fs-5 mb-2'>
                  <span className='fw-bold'>Total repayment</span>
                  <span className='fw-bold'>{formatCurrency(selectedMetrics.totalPayment)}</span>
                </div>
                <div className='d-flex justify-content-between fs-5'>
                  <span className='fw-bold'>Total interest</span>
                  <span className='fw-bold'>{formatCurrency(selectedMetrics.totalInterest)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {step === 'success' ? (
        <div className='d-flex mt-10'>
          <div className='card border border-gray-200 shadow-sm w-100'>
            <div className='card-body p-8 p-lg-15 text-center'>
              <div className='w-100px h-100px rounded-circle bg-light-success d-inline-flex align-items-center justify-content-center mb-5'>
                <i className='bi bi-check-circle-fill text-success fs-1' />
              </div>
              <h2 className='fw-bold text-gray-900 mb-3 fs-1'>Application submitted</h2>
              <p className='fs-5 mb-10 fw-bold'>
                Your loan application has been sent and is now under review.
              </p>

              <div className='row g-4 text-start mb-10 w-75 mx-auto'>
                <div className='col-md-4'>
                  <div className='rounded bg-light p-6 h-100'>
                    <div className='text-uppercase fw-bold fs-8 mb-2 text-secondary'>Amount</div>
                    <div className='fw-bold fs-3 text-gray-900'>{formatCurrency(loanAmount)}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='rounded bg-light p-6 h-100'>
                    <div className='text-uppercase fw-bold fs-8 mb-2 text-secondary'>APR</div>
                    <div className='fw-bold fs-3 text-gray-900'>
                      {formatRate(selectedLoan.annualRate)}
                    </div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='rounded bg-light p-6 h-100'>
                    <div className='text-uppercase fw-bold fs-8 mb-2 text-secondary'>
                      Monthly payment
                    </div>
                    <div className='fw-bold fs-3 text-primary'>
                      {formatCurrency(selectedMetrics.monthlyPayment)}
                    </div>
                  </div>
                </div>
              </div>

              <div className='separator separator-dashed my-10 w-75 mx-auto' />

              <div className='text-start w-75 mx-auto mb-10'>
                <h3 className='fw-bold text-gray-900 mb-6'>What happens next</h3>
                <div className='d-flex flex-column gap-5'>
                  <div className='d-flex align-items-center gap-4'>
                    <div className='w-30px h-30px rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                      <span className='text-white fw-bold fs-7'>1</span>
                    </div>
                    <p className='fs-6 mb-0'>
                      Our team will review your application. This usually takes 1–2 business days.
                    </p>
                  </div>
                  <div className='d-flex align-items-center gap-4'>
                    <div className='w-30px h-30px rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                      <span className='text-white fw-bold fs-7'>2</span>
                    </div>
                    <p className='fs-6 mb-0'>
                      You may be contacted if additional information is needed.
                    </p>
                  </div>
                  <div className='d-flex align-items-center gap-4'>
                    <div className='w-30px h-30px rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                      <span className='text-white fw-bold fs-7'>3</span>
                    </div>
                    <p className='fs-6 mb-0'>
                      Once a decision is made, you’ll see the update here and receive an email
                      notification.
                    </p>
                  </div>
                </div>
              </div>

              <div className='d-flex flex-column flex-sm-row gap-4 justify-content-center mt-5'>
                <button className='btn btn-primary px-10' onClick={() => navigate('/finance')}>
                  Return to finance
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default BusinessLoanPage
